// @ts-nocheck
import React, { useState } from "react";
import SharedSelect from "../components/SharedSelect";
import "../styles/Home.css";
import "../styles/Create.css";
import { CHIPS } from "../data/listings";
import { useNavigate } from "react-router-dom";
import { createListing } from "../api/listings";
import { saveDraft } from "../api/drafts";
import { getUser } from "../auth/auth";
import { getSoldListings } from "../api/soldListings";
import { getCardMarket } from "../api/cards";
import LocationPickerModal from "../components/LocationPickerModal";
import { useToast } from "../components/ToastProvider";
import CardAutocompleteInput from "../components/CardAutocompleteInput";
import CardScanPanel from "../components/CardScanPanel";
import { canonicalGameToDisplayLabel } from "../utils/canonicalGames";
import "../styles/CardAutocomplete.css";
import {
  IMAGE_INPUT_ACCEPT_ATTR,
  MAX_IMAGE_BYTES_DEFAULT,
  validateImageFiles,
} from "../utils/imageUploadValidation";
import { formatLocationLabel, getProfileLocation } from "../utils/location";

const CONDITIONS = [
  { value: "DMG", label: "Damaged", desc: "Major damage" },
  { value: "HP", label: "Heavily Played", desc: "Heavy wear" },
  { value: "MP", label: "Moderately Played", desc: "Moderate wear" },
  { value: "LP", label: "Lightly Played", desc: "Light wear" },
  { value: "NM", label: "Near Mint", desc: "Near mint" },
];

const GAMES_WITH_SETS = {
  "Magic: The Gathering": ["Alpha", "Beta", "Unlimited", "Arabian Nights", "Legends", "The Brothers' War", "Phyrexia: All Will Be One"],
  "Pokémon": ["Base Set", "Jungle", "Fossil", "Team Rocket", "Gym Heroes", "Scarlet & Violet", "Crown Zenith", "Silver Tempest"],
  "Yu-Gi-Oh!": ["Legend of Blue Eyes", "Metal Raiders", "Pharaoh's Servant", "Labyrinth of Nightmare", "Legacy of Darkness"],
  "One Piece": ["Romance Dawn", "Paramount War", "Pillars of Strength", "Kingdoms of Intrigue", "Awakening of the New Era"]
};

export default function Create() {
  const navigate = useNavigate();
  const user = getUser();
  const profileLocation = getProfileLocation(user);
  const { showToast } = useToast();

  // Core fields
  const [listingType, setListingType] = useState("sale");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [game, setGame] = useState("");
  const [cardSet, setCardSet] = useState("");
  const [condition, setCondition] = useState("NM");
  const [price, setPrice] = useState("");
  const [acceptOffers, setAcceptOffers] = useState(false);
  const [tradeValue, setTradeValue] = useState("");
  const [lookingFor, setLookingFor] = useState("");
  const [shipsFrom, setShipsFrom] = useState("");
  const [handlingTime, setHandlingTime] = useState("1-2 days");
  const [returnsAccepted, setReturnsAccepted] = useState(false);
  const [images, setImages] = useState([]);
  
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(profileLocation);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [suggestedPrice, setSuggestedPrice] = useState("");
  const [selectedCard, setSelectedCard] = useState(null);
  const [marketRange, setMarketRange] = useState("");
  const [showSoldListings, setShowSoldListings] = useState(false);
  const [soldData, setSoldData] = useState(null);
  const [loadingSold, setLoadingSold] = useState(false);
  const [selectedGrading, setSelectedGrading] = useState("All");
  const [selectedVariant, setSelectedVariant] = useState("All");

  const normalizeGame = (value) => String(value || "").trim().toLowerCase();
  const isOnePieceGame = normalizeGame(game) === "one piece";
  const isCanonicalSupportedGame = (() => {
    const normalized = normalizeGame(game);
    return (
      normalized === "pokemon" ||
      normalized === "pokémon" ||
      normalized === "magic: the gathering" ||
      normalized === "mtg" ||
      normalized === "magic" ||
      normalized === "yu-gi-oh!" ||
      normalized === "yu-gi-oh" ||
      normalized === "yugioh"
    );
  })();
  const requiresCanonical = Boolean(game) && isCanonicalSupportedGame;


  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    const limit = 12 - images.length;

    const selected = files.slice(0, limit);
    const { validFiles, errors } = validateImageFiles(selected, { maxBytes: MAX_IMAGE_BYTES_DEFAULT });
    if (errors.length > 0) {
      setError(errors.join(" "));
    } else {
      setError("");
    }

    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setImages((prev) => [...prev, event.target.result]);
      };
      reader.readAsDataURL(file);
    });

    if (errors.length > 0) {
      e.target.value = "";
    }
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (images.length < 1) {
      return setError("At least one image is required (front and back recommended).");
    }

    if (!title.trim()) return setError("Title is required.");
    if (requiresCanonical && !selectedCard) return setError("Please select a valid card from suggestions.");
    if (!game) return setError("Please select a game/category.");
    if (!condition) return setError("Condition is required.");
    
    const p = Number(price);
    if ((listingType === "sale" || listingType === "both") && (!Number.isFinite(p) || p <= 0)) {
      return setError("Please enter a valid price.");
    }

    if ((listingType === "trade" || listingType === "both") && !tradeValue.trim()) {
      return setError("Trade value is required for trade listings.");
    }

    if ((listingType === "trade" || listingType === "both") && !lookingFor.trim()) {
      return setError("Please specify what you're looking for.");
    }

    // Check if user has location set for trade listings
    if (!selectedLocation && !profileLocation) {
      setShowLocationModal(true);
      return;
    }

    // Show confirmation modal before publishing
    setShowConfirmModal(true);
  };

  const createListingPost = async () => {
    const p = Number(price);
    try {
      await createListing({
        cardGame: selectedCard?.game,
        cardId: selectedCard?.cardId,
        cardNameCanonical: selectedCard?.cardName,
        title: title.trim(),
        description: description.trim(),
        game,
        set: cardSet.trim(),
        condition,
        price: (listingType === "sale" || listingType === "both") ? p : 0,
        shipsFrom: shipsFrom.trim(),
        image: images[0] || "https://via.placeholder.com/300x420/1a1a2e/e94560?text=Card+Image",
        images: images.length > 0 ? images : undefined,
        // Default values for other fields
        listingType,
        language: "English",
        rarity: "Other",
        isGraded: false,
        gradingCompany: "",
        grade: "",
        quantity: 1,
        acceptOffers: (listingType === "sale" || listingType === "both") ? acceptOffers : false,
        minimumOffer: 0,
        tradeValue: (listingType === "trade" || listingType === "both") ? tradeValue.trim() : "",
        lookingFor: (listingType === "trade" || listingType === "both") ? lookingFor.trim() : "",
        location: selectedLocation || profileLocation || undefined,
        shippingCost: "",
        handlingTime,
        returnsAccepted,
        proofLink: "",
      });
      navigate("/marketplace");
    } catch (err) {
      setError(err.message || "Failed to create listing");
    }
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    setError("");

    try {
      await saveDraft({
        listingType,
        title,
        description,
        game,
        cardSet,
        condition,
        price,
        acceptOffers,
        tradeValue,
        lookingFor,
        shipsFrom,
        handlingTime,
        returnsAccepted,
        images,
      });
      showToast("Draft saved successfully!", "success");
    } catch (err) {
      setError(err.message || "Failed to save draft");
    } finally {
      setSaving(false);
    }
  };

  const fee = price ? (Number(price) * 0.1).toFixed(2) : "0.00";
  const earnings = price ? (Number(price) - Number(fee)).toFixed(2) : "0.00";

  const progress = {
    photos: images.length > 0,
    card: Boolean(title.trim()) && Boolean(game),
    market: Number(price) > 0,
    delivery: Boolean(shipsFrom.trim()),
  };

  const validation = {
    hasCanonicalCard: requiresCanonical ? Boolean(selectedCard?.cardId) : true,
    hasImages: images.length >= 1,
    hasTitle: title.trim().length > 0,
    hasGame: Boolean(game),
    hasCondition: Boolean(condition),
    hasPrice: (listingType === "sale" || listingType === "both") ? Number(price) > 0 : true,
    hasTradeValue: (listingType === "trade" || listingType === "both") ? tradeValue.trim().length > 0 : true,
    hasLookingFor: (listingType === "trade" || listingType === "both") ? lookingFor.trim().length > 0 : true,
  };

  const canPost = Object.values(validation).every((v) => v);

  const handleViewSoldListings = async () => {
    if (!isCanonicalSupportedGame) {
      setError("Sold listings are unavailable for this game right now.");
      return;
    }
    if (!selectedCard?.cardId || !selectedCard?.game) {
      setError("Please select a valid card to view sold listings");
      return;
    }

    setLoadingSold(true);
    setError("");
    
    try {
      const data = await getSoldListings({ game: selectedCard.game, cardId: selectedCard.cardId });
      setSoldData(data);
      setShowSoldListings(true);
    } catch (err) {
      setError("Failed to load sold listings");
    } finally {
      setLoadingSold(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  React.useEffect(() => {
    let active = true;
    const loadMarket = async () => {
      if (!isCanonicalSupportedGame) {
        setMarketRange("Price unavailable");
        setSuggestedPrice("");
        return;
      }
      if (!selectedCard?.cardId || !selectedCard?.game) {
        setMarketRange("");
        setSuggestedPrice("");
        return;
      }
      try {
        const data = await getCardMarket({ game: selectedCard.game, cardId: selectedCard.cardId });
        if (!active) return;
        const low = data?.market?.low;
        const high = data?.market?.high;
        const mid = data?.market?.mid ?? data?.market?.market;
        if (low != null && high != null) {
          setMarketRange(`Market range: $${low}-$${high}`);
        } else {
          setMarketRange("Price unavailable");
        }
        if (mid != null) {
          setSuggestedPrice(String(mid));
        } else {
          setSuggestedPrice("");
        }
      } catch {
        if (!active) return;
        setMarketRange("Price unavailable");
        setSuggestedPrice("");
      }
    };
    void loadMarket();
    return () => {
      active = false;
    };
  }, [selectedCard, isCanonicalSupportedGame]);

  return (
    <div className="create-listing-page">
      <div className="create-shell">
        <div className="create-header">
          <button className="back-btn" onClick={() => navigate("/marketplace")}>
            ←
          </button>
          <div>
            <h1>List a Card</h1>
            <p>Market-grade listings for serious collectors.</p>
          </div>
        </div>

        <div className="progress-card">
          <div className="progress-title">Listing Progress</div>
          <div className="progress-steps">
            <span className={`progress-step ${progress.photos ? "done" : ""}`}>Photos</span>
            <span className={`progress-step ${progress.card ? "done" : ""}`}>Card</span>
            <span className={`progress-step ${progress.market ? "done" : ""}`}>Market</span>
            <span className={`progress-step ${progress.delivery ? "done" : ""}`}>Delivery</span>
          </div>
        </div>

        <form className="create-form" onSubmit={onSubmit}>
          <section className="section-card hero">
            <div className="section-header">
              <h2>Listing Type</h2>
              <p>Make this impossible to miss.</p>
            </div>
            <div className="listing-type">
              <button
                type="button"
                className={`pill ${listingType === "sale" ? "active" : ""}`}
                onClick={() => setListingType("sale")}
              >
                💰 For Sale
              </button>
              <button
                type="button"
                className={`pill ${listingType === "trade" ? "active" : ""}`}
                onClick={() => setListingType("trade")}
              >
                🔁 For Trade
              </button>
              <button
                type="button"
                className={`pill ${listingType === "both" ? "active" : ""}`}
                onClick={() => setListingType("both")}
              >
                ⚡ Sale or Trade
              </button>
            </div>
          </section>

          <section className="section-card">
            <div className="section-header">
              <h2>Photos</h2>
              <p>Front and back help build trust. {images.length}/12 uploaded</p>
            </div>
            <div className="upload-section">
              <input
                type="file"
                id="imageUpload"
                accept={IMAGE_INPUT_ACCEPT_ATTR}
                multiple
                onChange={handleImageUpload}
                style={{ display: "none" }}
              />
              <label htmlFor="imageUpload" className="upload-area">
                {images.length === 0 ? (
                  <div className="upload-placeholder">
                    <div className="upload-icon">📷</div>
                    <div className="upload-text">Add up to 12 photos</div>
                    <div className="upload-hint">or drag and drop</div>
                  </div>
                ) : (
                  <div className="images-grid">
                    {images.map((img, i) => (
                      <div key={i} className="image-preview">
                        <img src={img} alt={`Upload ${i + 1}`} />
                        <button
                          type="button"
                          className="remove-img"
                          onClick={(e) => {
                            e.preventDefault();
                            removeImage(i);
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    {images.length < 12 && (
                      <div className="add-more">
                        <div className="upload-icon">+</div>
                      </div>
                    )}
                  </div>
                )}
              </label>
            </div>
          </section>

          <section className="section-card">
            <div className="section-header">
              <h2>Card Identity</h2>
              <p>Name it clearly for search and trust.</p>
            </div>
            <CardScanPanel
              flowContext="listing"
              gameHint={game}
              title="Scan Card Identity"
              subtitle="Upload front and back images to detect the card first, then confirm or edit the result before listing."
              onConfirmed={(confirmedCard) => {
                setSelectedCard(confirmedCard);
                setTitle(confirmedCard?.cardName || "");
                setGame(canonicalGameToDisplayLabel(confirmedCard?.game) || "");
                setCardSet(confirmedCard?.setName || "");
              }}
            />
            <div className="form-group">
              <div className="char-count">{title.length}/80</div>
              <CardAutocompleteInput
                id="marketplaceCardName"
                game={game}
                value={title}
                onValueChange={(nextValue) => setTitle(nextValue.slice(0, 80))}
                selectedCard={selectedCard}
                onSelectCard={setSelectedCard}
                placeholder="Search and select card"
                required
              />
              {isOnePieceGame && (
                <div className="subtext">One Piece canonical validation coming soon.</div>
              )}
            </div>
          </section>

          <section className="section-card">
            <div className="section-header">
              <h2>Game & Set</h2>
              <p>Collectors filter hard. Be precise.</p>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Game/Category</label>
                <SharedSelect value={game} onChange={(e) => {
                  setGame(e.target.value);
                  setCardSet(""); // Reset set when game changes
                  setSelectedCard(null);
                }}>
                  <option value="">Select game</option>
                  {CHIPS.filter((x) => x !== "All").map((g) => (
                    <option key={g} value={g}>
                      {g}
                    </option>
                  ))}
                </SharedSelect>
              </div>
              <div className="form-group">
                <label>Set/Series {game && `(${game})`}</label>
                {game && GAMES_WITH_SETS[game] && GAMES_WITH_SETS[game].length > 0 ? (
                  <>
                    <SharedSelect 
                      value={cardSet} 
                      onChange={(e) => setCardSet(e.target.value)}
                      style={{ marginBottom: '8px' }}
                    >
                      <option value="">Select a set</option>
                      {GAMES_WITH_SETS[game].map((set) => (
                        <option key={set} value={set}>
                          {set}
                        </option>
                      ))}
                    </SharedSelect>
                    <input
                      type="text"
                      value={cardSet}
                      onChange={(e) => setCardSet(e.target.value)}
                      placeholder="Or type custom set name"
                      style={{ fontSize: '13px', opacity: 0.8 }}
                    />
                  </>
                ) : (
                  <input
                    type="text"
                    value={cardSet}
                    onChange={(e) => setCardSet(e.target.value)}
                    placeholder="e.g., Kingdoms of Intrigue"
                  />
                )}
              </div>
            </div>
          </section>

          <section className="section-card">
            <div className="section-header">
              <h2>Card State</h2>
              <p>Horizontal rarity bar for fast selection.</p>
            </div>
            <div className="condition-bar">
              {CONDITIONS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  className={`condition-seg ${condition === c.value ? "active" : ""}`}
                  onClick={() => setCondition(c.value)}
                  title={c.desc}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </section>

          <section className="section-card">
            <div className="section-header">
              <h2>Market Value</h2>
              <p>Set your market value for serious buyers.</p>
            </div>
            {(listingType === "sale" || listingType === "both") && (
              <div className="form-grid">
                <div className="form-group">
                  <div className="price-input-wrapper">
                    <span className="currency">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      placeholder="Set your market value"
                      className="price-input"
                    />
                  </div>
                  {marketRange && <div className="subtext">{marketRange}</div>}
                  {suggestedPrice && (
                    <div className="price-suggestion">
                      <span>Suggested market price: ${suggestedPrice}</span>
                      <button 
                        type="button"
                        className="btn-use-price"
                        onClick={() => {
                          setPrice(suggestedPrice);
                        }}
                      >
                        Use This
                      </button>
                    </div>
                  )}
                  <div className="sold-listings-action">
                    <button 
                      type="button" 
                      className="btn-view-sold"
                      onClick={handleViewSoldListings}
                      disabled={loadingSold}
                    >
                      {loadingSold ? "Loading..." : "📊 View Sold Listings"}
                    </button>
                  </div>
                  {!isCanonicalSupportedGame && (
                    <div className="subtext">Price unavailable for this game right now.</div>
                  )}
                </div>
                <div className="form-group">
                  <button
                    type="button"
                    className={`toggle ${acceptOffers ? "on" : ""}`}
                    onClick={() => setAcceptOffers((prev) => !prev)}
                  >
                    {acceptOffers ? "On" : "Off"}
                  </button>
                </div>
              </div>
            )}

            {(listingType === "trade" || listingType === "both") && (
              <div className="form-grid trade-grid">
                <div className="form-group">
                  <input
                    type="text"
                    value={tradeValue}
                    onChange={(e) => setTradeValue(e.target.value)}
                    placeholder="e.g., $120–$150 value"
                  />
                </div>
                <div className="form-group">
                  <input
                    type="text"
                    value={lookingFor}
                    onChange={(e) => setLookingFor(e.target.value)}
                    placeholder="e.g., Alt arts, slabs, sealed"
                  />
                </div>
              </div>
            )}

            {(listingType === "sale" || listingType === "both") && price && (
              <div className="price-breakdown">
                <div className="breakdown-row">
                  <span>Item price</span>
                  <span>${Number(price).toFixed(2)}</span>
                </div>
                <div className="breakdown-row fee">
                  <span>Platform fee (10%)</span>
                  <span>-${fee}</span>
                </div>
                <div className="breakdown-row total">
                  <span>You earn</span>
                  <span>${earnings}</span>
                </div>
              </div>
            )}
          </section>

          <section className="section-card">
            <div className="section-header">
              <h2>Delivery & Trust</h2>
              <p>Faster handling = higher buyer confidence.</p>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <input
                  type="text"
                  value={shipsFrom}
                  onChange={(e) => setShipsFrom(e.target.value)}
                  placeholder="ZIP or Country"
                />
              </div>
              <div className="form-group">
                <SharedSelect value={handlingTime} onChange={(e) => setHandlingTime(e.target.value)}>
                  <option value="1-2 days">1-2 days</option>
                  <option value="3-5 days">3-5 days</option>
                  <option value="1 week">1 week</option>
                  <option value="2 weeks">2 weeks</option>
                </SharedSelect>
              </div>
              <div className="form-group">
                <button
                  type="button"
                  className={`toggle ${returnsAccepted ? "on" : ""}`}
                  onClick={() => setReturnsAccepted((prev) => !prev)}
                >
                  {returnsAccepted ? "Yes" : "No"}
                </button>
              </div>
            </div>
            <div className="form-group" style={{ marginTop: "16px" }}>
              <label>Listing Location</label>
              <div className="subtext" style={{ marginBottom: "8px" }}>
                {formatLocationLabel(selectedLocation || profileLocation, "No listing location selected")}
              </div>
              <div className="location-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowLocationModal(true)}>
                  {(selectedLocation || profileLocation) ? "Change location" : "Set location"}
                </button>
                {profileLocation ? (
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setSelectedLocation(profileLocation)}
                  >
                    Use profile location
                  </button>
                ) : null}
              </div>
              <div className="subtext">Only post cards that are currently available in this area.</div>
            </div>
          </section>

          <section className="section-card">
            <div className="section-header">
              <h2>Seller Notes</h2>
              <p>Optional details that build confidence.</p>
            </div>
            <div className="form-group">
              <div className="char-count">{description.length}/1000</div>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value.slice(0, 1000))}
                placeholder="Condition notes, serials, provenance, etc."
                rows={5}
                maxLength={1000}
              />
            </div>
          </section>

          {error && <div className="error-message">{error}</div>}

          {!canPost && (
            <div className="validation-checklist">
              <div className="validation-title">⚠️ Complete these before posting:</div>
              <div className="validation-items">
                {!validation.hasCanonicalCard && <div className="validation-item">• Select a valid card from suggestions</div>}
                {!validation.hasImages && <div className="validation-item">• Add at least 1 image</div>}
                {!validation.hasTitle && <div className="validation-item">• Enter card title</div>}
                {!validation.hasGame && <div className="validation-item">• Select game/category</div>}
                {!validation.hasCondition && <div className="validation-item">• Choose condition</div>}
                {!validation.hasPrice && <div className="validation-item">• Set price</div>}
                {!validation.hasTradeValue && <div className="validation-item">• Enter trade value</div>}
                {!validation.hasLookingFor && <div className="validation-item">• Specify what you're looking for</div>}
              </div>
            </div>
          )}

          <div className="form-actions">
            <button 
              type="button" 
              className="btn-secondary" 
              onClick={handleSaveDraft}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Draft"}
            </button>
            <button type="submit" className="btn-submit" disabled={!canPost}>
              {canPost ? "Post to Market" : "Complete Required Fields"}
            </button>
          </div>
        </form>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>🚀 Ready to Publish?</h2>
            <p>Please review your listing details before posting:</p>
            
            <div className="listing-summary">
              <div className="summary-item">
                <span className="summary-label">Title:</span>
                <span className="summary-value">{title}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Game:</span>
                <span className="summary-value">{game}</span>
              </div>
              {cardSet && (
                <div className="summary-item">
                  <span className="summary-label">Set:</span>
                  <span className="summary-value">{cardSet}</span>
                </div>
              )}
              <div className="summary-item">
                <span className="summary-label">Condition:</span>
                <span className="summary-value">{condition}</span>
              </div>
              {(listingType === "sale" || listingType === "both") && (
                <div className="summary-item">
                  <span className="summary-label">Price:</span>
                  <span className="summary-value">${price}</span>
                </div>
              )}
              <div className="summary-item">
                <span className="summary-label">Type:</span>
                <span className="summary-value">{listingType === "both" ? "Sale & Trade" : listingType === "sale" ? "For Sale" : "Trade Only"}</span>
              </div>
            </div>
            
            <div className="modal-actions">
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={() => setShowConfirmModal(false)}
              >
                Go Back
              </button>
              <button 
                type="button" 
                className="btn-primary" 
                onClick={() => {
                  setShowConfirmModal(false);
                  createListingPost();
                }}
              >
                Confirm & Post
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Sold Listings Modal */}
      {showSoldListings && soldData && (() => {
        // Get unique variants and grading companies
        const variants = ["All", ...new Set(soldData.listings.map(l => l.variant))];
        const gradingCompanies = ["All", "PSA", "CGC", "TAG", "BGS", "ACE"];
        
        // Filter listings
        const filteredListings = soldData.listings.filter(listing => {
          const variantMatch = selectedVariant === "All" || listing.variant === selectedVariant;
          const gradingMatch = selectedGrading === "All" || listing.gradingCompany === selectedGrading;
          return variantMatch && gradingMatch;
        });
        
        // Get grade boxes (unique grades with prices)
        const gradeMap = {};
        filteredListings.forEach(listing => {
          if (!gradeMap[listing.grade] || listing.price < gradeMap[listing.grade]) {
            gradeMap[listing.grade] = listing.price;
          }
        });
        const gradeBoxes = Object.entries(gradeMap)
          .sort((a, b) => parseFloat(b[0]) - parseFloat(a[0]))
          .slice(0, 5);
        
        return (
          <div className="modal-overlay" onClick={() => {
            setShowSoldListings(false);
            setSelectedGrading("All");
            setSelectedVariant("All");
          }}>
            <div className="modal-content sold-modal" onClick={(e) => e.stopPropagation()}>
              <div className="sold-header">
                <h2>Sold Listings</h2>
                <button 
                  className="modal-close" 
                  onClick={() => {
                    setShowSoldListings(false);
                    setSelectedGrading("All");
                    setSelectedVariant("All");
                  }}
                >
                  ✕
                </button>
              </div>
              
              <div className="card-title-display">
                {title || "Card Title"}
              </div>
              
              {filteredListings.length > 0 ? (
                <>
                  <div className="sold-filters">
                    <div className="filter-group">
                      <label className="filter-label">Variant</label>
                      <SharedSelect 
                        className="filter-select"
                        value={selectedVariant}
                        onChange={(e) => setSelectedVariant(e.target.value)}
                      >
                        {variants.map(v => (
                          <option key={v} value={v}>{v}</option>
                        ))}
                      </SharedSelect>
                    </div>
                    <div className="filter-group">
                      <label className="filter-label">Type</label>
                      <SharedSelect 
                        className="filter-select"
                        value={selectedGrading}
                        onChange={(e) => setSelectedGrading(e.target.value)}
                      >
                        {gradingCompanies.map(g => (
                          <option key={g} value={g}>{g}</option>
                        ))}
                      </SharedSelect>
                    </div>
                  </div>

                  {gradeBoxes.length > 0 && (
                    <div className="grade-boxes">
                      {gradeBoxes.map(([grade, price]) => (
                        <div key={grade} className="grade-box">
                          <div className="grade-number">{grade}</div>
                          <div className="grade-price">${price.toFixed(0)}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="sold-note">
                    Note: Cardex may be compensated if you make a purchase.
                  </div>

                  <div className="sold-listings-list">
                    {filteredListings.map((listing) => {
                      const isExternal = listing.marketplace !== "Cardex";
                      return (
                        <div key={listing.id} className="sold-listing-card">
                          <div className="listing-left">
                            <div className={`marketplace-logo ${listing.marketplace === "eBay" ? "ebay-bg" : listing.marketplace === "TCGPlayer" ? "tcgplayer-bg" : "cardex-bg"}`}>
                              {listing.marketplace === "eBay" && (
                                <span className="logo-text ebay">ebay</span>
                              )}
                              {listing.marketplace === "TCGPlayer" && (
                                <span className="logo-text tcgplayer">
                                  <span>TCG</span>
                                  <span>player</span>
                                </span>
                              )}
                              {listing.marketplace === "Cardex" && (
                                <span className="logo-text cardex">CARDEX</span>
                              )}
                            </div>
                            <div className="listing-info">
                              <div className="listing-type">{listing.saleType}</div>
                              <div className="listing-date">{formatDate(listing.soldDate)}</div>
                              {listing.grade && (
                                <div className="listing-grade">
                                  {listing.gradingCompany} {listing.grade}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="listing-right">
                            <div className="listing-price">${listing.price.toFixed(0)}</div>
                            <button 
                              className="listing-arrow"
                              onClick={() => {
                                if (isExternal) {
                                  window.open(listing.listingUrl, '_blank');
                                } else {
                                  // Navigate to internal listing
                                  window.location.href = listing.listingUrl;
                                }
                              }}
                            >
                              →
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <button 
                    className="btn-close-modal"
                    onClick={() => {
                      setShowSoldListings(false);
                      setSelectedGrading("All");
                      setSelectedVariant("All");
                    }}
                  >
                    Close
                  </button>
                </>
              ) : (
                <div className="no-data">
                  <p>No sold listings found</p>
                  <p className="subtext">Try changing the filters or search for a different card.</p>
                </div>
              )}
            </div>
          </div>
        );
      })()}
      <LocationPickerModal
        isOpen={showLocationModal}
        title="Listing Location"
        subtitle="Choose where this listing is physically available. Shipping origin stays separate."
        confirmLabel="Use this listing location"
        initialLocation={selectedLocation || profileLocation}
        onClose={() => setShowLocationModal(false)}
        onConfirm={async (location) => {
          setSelectedLocation(location);
        }}
      />
    </div>
  );
}
